import { bump, fmtCompact, tickNumber } from "../kit";
import { FLY_TIMING, Point, burstCount, volleyTimings } from "./flyMath";
import { playVolley } from "./flyLayer";
import { Gain, Holdings, diffHoldings, groupGains, mergeGains } from "./holdings";
import { REWARD_HOMES, REWARD_KINDS, RewardKind } from "./rewardHomes";
import { rewardIconUrl } from "./rewardIcons";
import { HomeHit, findHome, liveSpot } from "./targetPick";

/**
 * Reward fly-to-home (owner 2026-10-24): whatever the player gains flies from where it was earned into the button or
 * counter where it lives, the home pops as each icon lands, and its counter ticks up to the new total.
 *
 * One hook for every grant path: after each save (features/rewardFly.ts listens to the registry's `persisted`) the
 * holdings are compared with the last snapshot (holdings.ts), so a chest, a battle's reward, a Shop or Store purchase,
 * a daily or VIP claim, a quest, pass or event reward and a pet catch all fly without screen-by-screen code. Where it was
 * earned: the element a screen names (`noteOrigin`, purchaseFx/ordersSheet do), else the button just tapped, else the
 * middle of the screen. Gains made away from the hub (in a battle, on its end screen) wait and fly when the hub is back,
 * to where their homes are then. Presentation only: the numbers are already in the save; this decides what is seen.
 */

export const FLY = {
  /** A tap or a named origin older than this is not where the reward came from. */
  ORIGIN_TTL_MS: 2500,
  /** Gains parked away from the hub longer than this are stale (the player went battle → battle) and are dropped. */
  PENDING_MAX_AGE_MS: 120_000,
  /** The hub's cloud wipe opens in this long (HubView.LootFlightDelayMs): parked gains fly once it has. */
  HUB_ENTRY_DELAY_MS: 650,
  /** With nothing tapped, rewards rise from here (share of the viewport's height, centred). */
  DEFAULT_Y_SHARE: 0.55,
  /** Icon size: unique items read a little bigger than coins. */
  ICON_PX: 28,
  ITEM_ICON_PX: 36,
  /** The home's pop as an icon lands. */
  BUMP_PEAK: 1.16,
  BUMP_MS: 170,
  /** The counter keeps rolling this long after the last icon lands. */
  TICK_TAIL_MS: 120,
  /** While a chest is being opened over the screen, the flight looks again this often (it flies once COLLECT is tapped). */
  HELD_RETRY_MS: 250,
} as const;

export interface RewardFlyDeps {
  /** The holdings now (holdings.ts readHoldings on the game's catalog). */
  read(): Holdings;
  /** False under Reduce Motion or Settings → reduced effects: nothing flies (the counters already show the totals). */
  motionOk(): boolean;
  /** One icon landed (the n-th of its volley): a short clink. */
  landSound(n: number): void;
  /** The hub is on screen (gains wait for it otherwise). */
  hubUp(): boolean;
  /** Something plays the reward first (a chest opening over the screen): the flight waits until it is gone. */
  held(): boolean;
  now(): number;
}

interface Origin { readonly el: Element | null; readonly x: number; readonly y: number; readonly at: number }

export class RewardFly {
  private last: Holdings;
  private pending: Gain[] = [];
  private pendingAt = 0;
  private frame = 0;
  private hint: Origin | null = null;
  private tap: Origin | null = null;

  constructor(private readonly d: RewardFlyDeps) { this.last = d.read(); }

  /** Compare the holdings with the last snapshot; anything gained flies on the next frame. Safe to call any time. */
  check(): void {
    const now = this.d.read(), gains = diffHoldings(this.last, now);
    this.last = now;
    if (gains.length === 0) return;
    if (!this.d.motionOk()) { this.pending = []; return; }
    this.pending = mergeGains(this.pending, gains);
    this.pendingAt = this.d.now();
    this.schedule();
  }

  /**
   * A screen names where the next gain comes from (the card bought, the claim button). Its spot is read now: the
   * screen usually redraws right after a claim, and the button is gone by the time the flight starts.
   */
  noteOrigin(el: Element | null): void {
    if (!el) return;
    const r = el.isConnected ? el.getBoundingClientRect() : null;
    this.hint = { el, x: r && r.width > 0 ? r.left + r.width / 2 : NaN, y: r && r.width > 0 ? r.top + r.height / 2 : NaN, at: this.d.now() };
  }

  /** The kinds waiting to fly (gained away from the hub, or held behind a chest). */
  waiting(): readonly RewardKind[] { return [...new Set(this.pending.map((g) => g.kind))]; }

  /** A tap anywhere in the UI (a click caught on its way down): the default origin for a gain that follows. */
  noteTap(el: Element | null, x: number, y: number): void { this.tap = { el, x, y, at: this.d.now() }; }

  /** The hub was drawn: gains parked while it was away fly once its entry wipe is open, from the screen's middle. */
  hubShown(): void {
    if (this.pending.length === 0) return;
    this.hint = this.tap = null;
    setTimeout(() => this.schedule(), FLY.HUB_ENTRY_DELAY_MS);
  }

  private schedule(): void {
    if (this.frame !== 0) return;
    // the next frame: the screen that granted has re-rendered by then, and all reads happen before any write
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.launch(); });
  }

  private launch(): void {
    if (this.pending.length === 0) return;
    if (this.d.now() - this.pendingAt > FLY.PENDING_MAX_AGE_MS) { this.pending = []; return; }
    if (!this.d.hubUp()) return;                                   // parked until hubShown
    if (this.d.held()) { setTimeout(() => this.schedule(), FLY.HELD_RETRY_MS); return; }
    const gains = this.pending;
    this.pending = [];
    // read phase: the origin and every home's spot, before a single style is written
    const origin = this.originPoint();
    const groups = groupGains(gains);
    const plans: { kind: RewardKind; list: Gain[]; hit: HomeHit }[] = [];
    for (const kind of REWARD_KINDS) {
      const list = groups.get(kind), hit = list ? findHome(REWARD_HOMES[kind].home) : null;
      if (list && hit) plans.push({ kind, list, hit });
    }
    // write phase
    plans.forEach((p, k) => this.fly(p.kind, p.list, p.hit, origin, k * FLY_TIMING.KIND_GAP_MS));
  }

  private originPoint(): Point {
    const now = this.d.now(), fresh = (o: Origin | null) => (o && now - o.at <= FLY.ORIGIN_TTL_MS ? o : null);
    // the freshest of the named spot and the last tap (after a chest's COLLECT, the tap on COLLECT)
    const h = fresh(this.hint), t = fresh(this.tap), o = h && t ? (t.at > h.at ? t : h) : h ?? t;
    this.hint = this.tap = null;
    if (o?.el?.isConnected) {
      const r = o.el.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
    if (o && Number.isFinite(o.x)) return { x: o.x, y: o.y };
    return { x: innerWidth / 2, y: innerHeight * FLY.DEFAULT_Y_SHARE };
  }

  private fly(kind: RewardKind, list: readonly Gain[], hit: HomeHit, origin: Point, delayMs: number): void {
    const unique = REWARD_HOMES[kind].unique;
    const amount = list.reduce((a, g) => a + g.amount, 0);
    const n = burstCount(unique ? list.length : amount, unique);
    const icons = Array.from({ length: n }, (_, i) => rewardIconUrl(list[i % list.length]));
    // a counter (gems, gold…) shows what it held before until the first icon lands, then rolls up to the total
    const total = list.length === 1 ? list[0].total : NaN, from = total - amount;
    if (hit.counter && Number.isFinite(total)) hit.counter.textContent = fmtCompact(from);
    const t = volleyTimings(n), rollMs = t.length > 0 ? t[t.length - 1].land - t[0].land + FLY.TICK_TAIL_MS : 0;
    const flew = playVolley({
      origin, target: { x: hit.x, y: hit.y }, icons, sizePx: unique ? FLY.ITEM_ICON_PX : FLY.ICON_PX, delayMs,
      onLand: (i) => {
        const live = liveSpot(hit);
        bump(live?.el ?? null, FLY.BUMP_PEAK, FLY.BUMP_MS);
        this.d.landSound(i);
        if (i === 0 && live?.counter && Number.isFinite(total)) tickNumber(live.counter, from, total, rollMs, fmtCompact);
      },
    });
    if (flew === 0 && hit.counter && Number.isFinite(total)) hit.counter.textContent = fmtCompact(total);
  }
}
